import {
  ChangeEvent,
  CSSProperties,
  DetailedHTMLProps,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  SelectHTMLAttributes,
  useRef,
  useState,
} from "react";
import { styles } from "./SelectInput.style";
import { keyboardAction, KeyType } from "../../core/utils/keyboardActions";
import { Icon, Icons } from "../Icon";
import { ClearIcon } from "../Input/icons/ClearIcon/ClearIcon";
import { createMouseEnterUnifiedHandler } from "../../core/utils/createMouseEnterUnifiedHandler";
import { cx } from "../../../styled-system/css";

export type SelectInputSelectEvent =
  MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLUListElement> | { target: { value: string; dataset: DOMStringMap } };

export interface SelectInputProps extends Omit<
  DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
  "onSelect"
> {
  label?: string;
  message?: string;
  hasError?: boolean;
  isSuccess?: boolean;
  withSuccessIcon?: boolean;
  customFrontIcon?: ReactNode;
  isShort?: boolean;
  placeholder?: string;
  testId?: string;
  hideAfterSelect?: boolean;
  withInputField?: boolean;
  inputValue?: string;
  onChangeInput?: (input: string) => void;
  onBlurInput?: () => void;
  inputAriaLabel: string;
  customEndIcon?: ReactNode;
  onSelect?: (event: SelectInputSelectEvent) => void;
  customStyles?: string;
  onClick?: (event: MouseEvent) => void;
  onClear?: () => void;
  ariaLabelClearButton?: string;
  children?: ReactNode;
}

export const SelectInput = (props: SelectInputProps) => {
  const {
    label,
    children,
    message,
    id,
    disabled,
    hasError,
    customFrontIcon,
    placeholder,
    isShort,
    testId,
    hideAfterSelect,
    withInputField,
    inputValue = "",
    onChangeInput,
    onBlurInput,
    inputAriaLabel,
    isSuccess = false,
    withSuccessIcon = false,
    customEndIcon,
    onSelect = () => {},
    customStyles,
    onClear,
    ariaLabelClearButton = "Clear text field",
  } = props;
  const [showSelectDropdown, setShowSelectDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const selectInputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const focusMainElement = () => {
    if (withInputField) {
      inputRef.current?.focus();
    } else {
      divRef.current?.focus();
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setShowSelectDropdown(!showSelectDropdown);
      focusMainElement();
    }
  };

  const handleSelect = (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLUListElement>) => {
    onSelect(event);
    if (hideAfterSelect) {
      setShowSelectDropdown(false);
    }
    focusMainElement();
  };

  const arrowKeysHandler = (event: KeyboardEvent<HTMLInputElement | HTMLUListElement>) => {
    const allItems = Array.from(menuRef.current?.querySelectorAll("li") ?? []);
    const items = allItems.filter((item) => !item.hasAttribute("data-non-focusable"));

    const currentIndex = items.findIndex((item) => document.activeElement === item);

    keyboardAction(event, KeyType.ARROW_DOWN, () => {
      if (currentIndex === -1) {
        items[0]?.focus();
      } else {
        const nextIndex = (currentIndex + 1) % items.length;
        (items[nextIndex] as HTMLElement)?.focus();
      }
    });

    keyboardAction(event, KeyType.ARROW_UP, () => {
      if (currentIndex === -1) {
        (items[items.length - 1] as HTMLElement)?.focus();
      } else {
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        (items[prevIndex] as HTMLElement)?.focus();
      }
    });
  };

  const handleFieldFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!disabled) {
      const isFocusComingFromWithinContainer = selectInputRef.current?.contains(e.relatedTarget as Node);
      if (!isFocusComingFromWithinContainer) {
        setShowSelectDropdown(true);
        setIsFocused(true);
      }
    }
  };

  const handleFieldBlur = (e: FocusEvent<HTMLDivElement>) => {
    const isStillFocusedWithinContainer = selectInputRef.current?.contains(e.relatedTarget as Node);
    const isFocusingOnSelectInput = selectInputRef.current === e.relatedTarget;

    if (!isStillFocusedWithinContainer && !isFocusingOnSelectInput) {
      setShowSelectDropdown(false);
      setIsFocused(false);
      onBlurInput?.();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeInput?.(e.target.value);
    setShowSelectDropdown(true);
  };

  const handleClear = () => {
    onClear?.();
    onChangeInput?.("");
    onSelect?.({ target: { value: "", dataset: {} } });
    inputRef.current?.focus();
  };

  const handleMouseDown = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-clear-button]")) return;
    e.preventDefault();
    toggleDropdown();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    arrowKeysHandler(event);

    keyboardAction(event, KeyType.ENTER, () => {
      toggleDropdown();
    });
  };

  const handleKeyDownList = (event: KeyboardEvent<HTMLUListElement>) => {
    if (!menuRef.current) {
      return;
    }
    arrowKeysHandler(event);

    keyboardAction(event, KeyType.ENTER, () => {
      handleSelect(event);
    });
  };
  return (
    <div
      ref={selectInputRef}
      className={styles.root(isShort)}
      data-testid={testId}
      onFocusCapture={handleFieldFocus}
      onBlurCapture={handleFieldBlur}
      onMouseDownCapture={handleMouseDown}
      onClick={props.onClick}
    >
      <div
        className={cx(
          styles.form(),
          hasError && styles.withError(),
          disabled && styles.disabled(),
          isSuccess && styles.success(),
          customStyles,
        )}
      >
        <fieldset className={styles.content(showSelectDropdown, disabled, hasError)}>
          {customFrontIcon && <div className={styles.frontIcon(isShort, disabled)}>{customFrontIcon}</div>}
          <div className={styles.inputContainer()}>
            <label className={styles.labelText(!!customFrontIcon)} id={id}>
              {label}
            </label>
            {withInputField ? (
              <input
                ref={inputRef}
                aria-label={label ? label : inputAriaLabel}
                type="text"
                value={inputValue}
                onChange={handleChange}
                placeholder={placeholder}
                className={styles.input()}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
            ) : (
              <div
                tabIndex={disabled ? -1 : 0}
                className={styles.placeholderText(isShort, !!customFrontIcon, disabled)}
                ref={divRef}
                onKeyDown={handleKeyDown}
              >
                {placeholder}
              </div>
            )}
          </div>
          <div className={styles.endIconWrapper()}>
            {inputValue && isFocused && (
              <div className={styles.endIcon()}>
                <button
                  data-clear-button
                  tabIndex={0}
                  aria-label={ariaLabelClearButton}
                  type="button"
                  {...createMouseEnterUnifiedHandler(handleClear)}
                >
                  <ClearIcon />
                </button>
              </div>
            )}
            {hasError && !isSuccess && (
              <div className={cx(styles.endIcon(), styles.errorIcon())}>
                <Icon icon={Icons.warning} />
              </div>
            )}
            {withSuccessIcon && isSuccess && !hasError && (
              <div className={styles.endIcon()}>
                <Icon icon={Icons.checkMarkGreenCircle} />
              </div>
            )}
            {customEndIcon && <div className={styles.endIcon()}>{customEndIcon}</div>}
            <div
              className={styles.selectArrow(showSelectDropdown, disabled, isShort)}
              style={{ "--arrow-icon": `url(${Icons.dropdownChevron})` } as CSSProperties}
            />
          </div>
        </fieldset>
        {!isShort && !!message && <div className={styles.info(!!message)}>{message}</div>}
      </div>

      <div
        className={styles.selectSpace(showSelectDropdown, isShort)}
        onMouseDownCapture={(event) => {
          event.preventDefault();
          handleSelect(event);
        }}
        tabIndex={-1}
      >
        <ul onKeyDownCapture={handleKeyDownList} ref={menuRef} tabIndex={-1}>
          {children}
        </ul>
      </div>
    </div>
  );
};
